import * as React from "react";
import { useCallback } from "react";
import { useErrorBoundary } from "react-error-boundary";
import { useNavigate } from "react-router-dom";
import { useCreateCaregiverByTemplateNameMutation } from "api";
import { isBioErrorResponse, isFetchBaseQueryError } from "api/types";
import { FormikHelpers } from "formik/dist/types";
import { isNil, isNotNil } from "ramda";

import { AffinityForm, FormView } from "affinityForm";
import { Layout } from "affinityUI/structures/layout";
import { UserConfirmation, UserForm } from "components/views/userEdit/";
import type { UserInfoCreateType } from "components/views/userEdit/types";
import { userInfoValidationSchema } from "components/views/userEdit/userForm/validationSchema";
import { useAuth } from "lib/auth";
import { careGiverErrorFieldConverter, careGiverReverseConverter } from "lib/biot/careGiver/converters";

export const AddUserPage: React.FC = () => {
  const { showBoundary: onError } = useErrorBoundary();
  const { currentUser } = useAuth()[1];
  const navigate = useNavigate();
  const [createUserMutation] = useCreateCaregiverByTemplateNameMutation();
  const handleCancel = useCallback(() => {
    navigate(`/users`);
  }, [navigate]);

  const createUser = useCallback(
    async (formData: UserInfoCreateType, formikHelpers: FormikHelpers<UserInfoCreateType>) => {
      if (isNil(currentUser) || isNil(currentUser.hospital)) {
        return;
      }
      try {
        await createUserMutation({
          templateName: formData.roleId === "admin" ? "dashboard_admin" : "Clinician",
          createCaregiverByTemplateNameRequest: {
            _ownerOrganization: {
              id: currentUser.hospital.id,
            },
            _email: formData.email,
            ...careGiverReverseConverter(formData),
          },
        }).unwrap();
        navigate(`/users`);
      } catch (error) {
        if (
          isFetchBaseQueryError(error) &&
          (error.status === 400 || error.status === 409) &&
          isBioErrorResponse(error.data) &&
          isNotNil(error.data.details)
        ) {
          formikHelpers.setFieldError("", error.data.message);

          Object.entries(error.data.details).forEach(([key, value]) =>
            formikHelpers.setFieldError(careGiverErrorFieldConverter(key) ?? key, value as unknown as string)
          );
          navigate(`/users/add`);
        } else {
          onError(error);
        }
      }
    },
    [currentUser, createUserMutation, navigate, onError]
  );

  return (
    <AffinityForm validationSchema={userInfoValidationSchema} confirmHandler={createUser}>
      <FormView
        form={
          <Layout title="Invite user" backLink="/users" showBackBtn>
            <UserForm onCancel={handleCancel} invitationAccepted={false} isCreate />
          </Layout>
        }
        confirm={
          <Layout title="Confirm and add user" backLink=".." showBackBtn>
            <UserConfirmation onCancel={handleCancel} />
          </Layout>
        }
      />
    </AffinityForm>
  );
};
